import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight, Linkedin, Twitter, MapPin, Instagram, Facebook, Youtube } from 'lucide-react';
import { useSiteSettings } from '@/contexts/SiteSettingsContext';
import { getCityUrl } from '@/data/urlMappings';
import BBBSeal from '@/components/BBBSeal';

const Footer = () => {
  const { settings } = useSiteSettings();
  const navigate = useNavigate();
  const location = useLocation();

  const scrollOrNavigate = (hash) => {
    const go = (smooth) => {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: smooth ? 'smooth' : 'instant' });
    };
    if (location.pathname === '/') {
      go(true);
      setTimeout(() => go(false), 1000);
      setTimeout(() => go(true), 2000);
    } else {
      navigate('/');
      setTimeout(() => go(false), 800);
      setTimeout(() => go(false), 1800);
      setTimeout(() => go(true), 2800);
    }
  };

  const serviceLinks = [
    { name: 'Managed IT Services', href: '/managed-it' },
    { name: 'Cybersecurity', href: '/cybersecurity' },
    { name: 'Cloud & M365', href: '/microsoft-365-support' },
    { name: 'Network', href: '/network' },
    { name: 'IT Strategy', href: '/it-strategy' },
    { name: 'Compliance', href: '/compliance' },
  ];

  const resourceLinks = [
    { name: 'Pricing', hash: '#calculator' },
    { name: 'Health Check', hash: '#health-check' },
    { name: 'Blog', href: '/blog' },
    { name: 'Case Studies', href: '/case-studies' },
    { name: 'ROI Calculator', href: '/roi-calculator' },
    { name: 'Industries', href: '/industries' },
    { name: 'Contact', href: '/contact-us' },
  ];

  const locationLinks = [
    { name: 'Vancouver', href: getCityUrl('vancouver') },
    { name: 'Burnaby', href: getCityUrl('burnaby') },
    { name: 'Surrey', href: getCityUrl('surrey') },
    { name: 'Richmond', href: getCityUrl('richmond') },
    { name: 'Coquitlam', href: getCityUrl('coquitlam') },
    { name: 'Abbotsford', href: getCityUrl('abbotsford') },
  ];

  return (
    <footer className="footer-gradient border-t border-white/5" data-testid="footer" data-dark>
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-10 gap-x-8 gap-y-10">
          {/* Brand */}
          <div className="col-span-2 md:col-span-3 lg:col-span-4">
            <Link to="/" className="flex items-center mb-6">
              <img 
                src={settings.logo_url || '/clickonemsp-logo.png'} 
                alt={settings.company_name || 'Click One MSP'} 
                className="h-20 lg:h-24 w-auto object-contain"
                width="96"
                height="96"
                onError={(e) => { e.target.src = '/clickonemsp-logo.png'; }}
              />
            </Link>
            <p className="text-gray-400 max-w-sm mb-6 text-sm leading-relaxed">
              {settings.tagline || 'Next-generation IT management and cybersecurity for businesses that refuse to be vulnerable.'}
            </p>
            <div className="space-y-2 text-sm text-gray-400">
              {settings.email && (
                <a href={`mailto:${settings.email}`} className="block hover:text-white transition-colors">
                  {settings.email}
                </a>
              )}
              {settings.phone && (
                <a href={`tel:${settings.phone}`} className="block hover:text-white transition-colors">
                  {settings.phone}
                </a>
              )}
              {settings.address && <p>{settings.address}</p>}
            </div>
            <div className="flex items-center gap-3 mt-6">
              <a href="https://www.linkedin.com/company/clickonemsp" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" data-testid="social-linkedin" className="w-9 h-9 border border-white/10 flex items-center justify-center hover:border-[#E63946] hover:bg-[#E63946]/10 transition-all">
                <Linkedin className="w-4 h-4 text-gray-400" />
              </a>
              <a href="https://www.instagram.com/clickonemsp" target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="social-instagram" className="w-9 h-9 border border-white/10 flex items-center justify-center hover:border-[#E63946] hover:bg-[#E63946]/10 transition-all">
                <Instagram className="w-4 h-4 text-gray-400" />
              </a>
              <a href="https://www.facebook.com/clickonemsp" target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="social-facebook" className="w-9 h-9 border border-white/10 flex items-center justify-center hover:border-[#E63946] hover:bg-[#E63946]/10 transition-all">
                <Facebook className="w-4 h-4 text-gray-400" />
              </a>
              <a href="https://m.youtube.com/@Clickonemsp" target="_blank" rel="noopener noreferrer" aria-label="YouTube" data-testid="social-youtube" className="w-9 h-9 border border-white/10 flex items-center justify-center hover:border-[#E63946] hover:bg-[#E63946]/10 transition-all">
                <Youtube className="w-4 h-4 text-gray-400" />
              </a>
              <a href="https://x.com/clickonemsp" target="_blank" rel="noopener noreferrer" aria-label="Twitter" data-testid="social-twitter" className="w-9 h-9 border border-white/10 flex items-center justify-center hover:border-[#E63946] hover:bg-[#E63946]/10 transition-all">
                <Twitter className="w-4 h-4 text-gray-400" />
              </a>
              <img src="/proudly-canadian.png" alt="Proudly Canadian" className="h-20 w-20 rounded-full object-cover" data-testid="proudly-canadian-badge" />
              <BBBSeal />
            </div>
          </div>

          {/* Services */}
          <div className="lg:col-span-2">
            <h4 className="text-white font-medium mb-4">Services</h4>
            <ul className="space-y-3">
              {serviceLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div className="lg:col-span-2">
            <h4 className="text-white font-medium mb-4">Resources</h4>
            <ul className="space-y-3">
              {resourceLinks.map((link) => (
                <li key={link.name}>
                  {link.comingSoon ? (
                    <span className="text-sm text-gray-500 flex items-center gap-2 cursor-default">
                      {link.name}
                      <span className="text-[9px] font-bold text-[#E63946] bg-[#E63946]/10 border border-[#E63946]/20 px-1 py-0.5 leading-none uppercase tracking-wider">Soon</span>
                    </span>
                  ) : link.href ? (
                    <Link
                      to={link.href}
                      className="text-sm text-gray-400 hover:text-white transition-colors"
                      data-testid={`footer-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      {link.name}
                    </Link>
                  ) : (
                    <button
                      onClick={() => scrollOrNavigate(link.hash)}
                      className="text-sm text-gray-400 hover:text-white transition-colors"
                      data-testid={`footer-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      {link.name}
                    </button>
                  )}
                </li>
              ))}
            </ul>
            <Link
              to="/contact-us"
              className="mt-6 flex items-center gap-2 text-[#E63946] text-sm font-medium hover:underline"
              data-testid="footer-cta"
            >
              Get Protected
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Areas We Serve */}
          <div className="lg:col-span-2">
            <h4 className="text-white font-medium mb-4">Areas We Serve</h4>
            <ul className="space-y-3">
              {locationLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-1.5"
                    data-testid={`footer-location-${link.name.toLowerCase()}`}
                  >
                    <MapPin className="w-3 h-3 text-[#E63946]" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/5 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-3 text-sm text-gray-500">
            <span>&copy; {new Date().getFullYear()} {settings.company_name || 'Click One MSP'}. All rights reserved.</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-gray-500">
            <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms-and-conditions" className="hover:text-white transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
